import { useRef, useState, useEffect } from "react";

/**
 * Read a runtime token emitted by the .copilotPage root in copilot.scss.
 *
 * Charting libraries need real color strings in a JS options object — they
 * cannot resolve `var(--cp-series-1)`. This is the ONLY sanctioned way to get
 * a token into JS; never restate a hex in a component.
 *
 * `el` should be a node inside .copilotPage so inherited custom properties
 * resolve. Falls back to document.documentElement, which returns "" for these
 * tokens rather than a wrong color — callers pass a fallback.
 */
/**
 * Find an element that actually carries the tokens.
 *
 * A component's own ref is null during its FIRST render, so `cssVar(name,
 * ref.current)` silently falls back on every call — which collapses a whole
 * ramp to one repeated colour. Fall back to the token host in the document
 * before giving up on the fallback value.
 */
const tokenHost = el => {
  if (el) return el;
  try {
    return document.querySelector(".copilotPage") || document.documentElement;
  } catch (e) {
    return document.documentElement;
  }
};

export const cssVar = (name, el, fallback = "") => {
  const node = tokenHost(el);
  try {
    const value = getComputedStyle(node).getPropertyValue(name);
    return (value || "").trim() || fallback;
  } catch (e) {
    return fallback;
  }
};

/**
 * Re-render once after mount so token reads happen with a live ref.
 *
 * Returns [ref, ready]. Attach the ref to the block's root; `ready` flips true
 * on mount, which re-runs the render body with ref.current populated. Without
 * this a chart keeps whatever colours it resolved before it existed.
 */
export const useTokenHost = () => {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(true);
  }, []);
  return [ref, ready];
};

export default cssVar;
